import { Box, Stack, Typography } from "@mui/material";
import { getAreaColor } from "components/identifyEventsTool/tool/ScaleWeightsChart.utils";
import { useAreaLabel } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMaterials } from "hooks/useMaterials";
import { useThrottledValue } from "hooks/useThrottledValue";
import { memo, useMemo } from "react";
import {
  ActiveChargeScheduleMaterials as ActiveChargeScheduleMaterialsType,
  useGetActiveChargeSchedulesQuery,
} from "src/store/api/generatedApi";

const CURRENT_TIME_THROTTLE_MS = 500;

interface ActiveChargeScheduleMaterialsProps {
  videoStart: Dayjs;
  videoEnd: Dayjs;
  location: string | undefined;
}

const orderByDropAreas = (
  areaMap: Map<string, ActiveChargeScheduleMaterialsType>,
  dropAreas: string[]
): ActiveChargeScheduleMaterialsType[] => {
  const areasInDropOrder = dropAreas.filter((area) => areaMap.has(area));
  const remainingAreas = [...areaMap.keys()].filter(
    (area) => !dropAreas.includes(area)
  );

  return [...areasInDropOrder, ...remainingAreas].flatMap((area) => {
    const entry = areaMap.get(area);
    return entry ? [entry] : [];
  });
};

const findActiveSchedulesByArea = (
  schedules: ActiveChargeScheduleMaterialsType[],
  currentTimeMs: number
): Map<string, ActiveChargeScheduleMaterialsType> => {
  return schedules
    .filter(
      (schedule) => dayjs(schedule.active_from).valueOf() <= currentTimeMs
    )
    .reduce((areaMap, schedule) => {
      const existing = areaMap.get(schedule.area);
      const scheduleActiveFrom = dayjs(schedule.active_from).valueOf();
      const existingActiveFrom = existing
        ? dayjs(existing.active_from).valueOf()
        : -Infinity;

      if (scheduleActiveFrom > existingActiveFrom) {
        areaMap.set(schedule.area, schedule);
      }
      return areaMap;
    }, new Map<string, ActiveChargeScheduleMaterialsType>());
};

const ActiveChargeScheduleMaterialsInner = ({
  videoStart,
  videoEnd,
  location,
}: ActiveChargeScheduleMaterialsProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { materials } = useMaterials();
  const { drop_areas: dropAreas } = useScrapEyeMetadata();
  const resolveAreaLabel = useAreaLabel(location);
  const { currentTime: currentTimeSeconds } = useVideoControlState();
  const throttledCurrentTimeSeconds = useThrottledValue(
    currentTimeSeconds,
    CURRENT_TIME_THROTTLE_MS
  );

  const startIso = useMemo(() => videoStart.toISOString(), [videoStart]);
  const endIso = useMemo(() => videoEnd.toISOString(), [videoEnd]);

  const { data: activeChargeSchedules } = useGetActiveChargeSchedulesQuery({
    tenantName,
    start: startIso,
    end: endIso,
  });

  const labellingMaterialNameMap = useMemo(
    () =>
      materials?.reduce<Record<string, string>>(
        (acc, material) => ({
          ...acc,
          [material.name]: material.labelling_name ?? material.name,
        }),
        {}
      ) ?? {},
    [materials]
  );

  const currentTimeMs = videoStart
    .add(throttledCurrentTimeSeconds, "second")
    .valueOf();

  const activeAtCurrentTime = useMemo(() => {
    if (!activeChargeSchedules?.charge_schedules) return [];

    const areaMap = findActiveSchedulesByArea(
      activeChargeSchedules.charge_schedules,
      currentTimeMs
    );

    return orderByDropAreas(areaMap, dropAreas);
  }, [activeChargeSchedules, dropAreas, currentTimeMs]);

  if (activeAtCurrentTime.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Typography variant="h5" color="primary">
        {t("activeChargeScheduleMaterialsTitle")}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {activeAtCurrentTime.map((schedule, index) => (
          <AreaMaterialsRow
            key={schedule.area}
            schedule={schedule}
            areaIndex={index}
            materialNameMap={labellingMaterialNameMap}
            areaLabel={resolveAreaLabel(schedule.area)}
          />
        ))}
      </Stack>
    </Box>
  );
};

export const ActiveChargeScheduleMaterials = memo(
  ActiveChargeScheduleMaterialsInner
);

interface AreaMaterialsRowProps {
  schedule: ActiveChargeScheduleMaterialsType;
  areaIndex: number;
  materialNameMap: Record<string, string>;
  areaLabel: string;
}

const AreaMaterialsRow = memo(
  ({
    schedule,
    areaIndex,
    materialNameMap,
    areaLabel,
  }: AreaMaterialsRowProps) => {
    const materialNames = useMemo(() => {
      if (schedule.charge_schedule_id === null) return [];
      if (schedule.planned_material_names === null) return null;
      return schedule.planned_material_names
        .map((name) => materialNameMap[name])
        .filter((name) => name !== undefined); // Filter out materials we don't use labelling names for
    }, [
      schedule.charge_schedule_id,
      schedule.planned_material_names,
      materialNameMap,
    ]);

    return (
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: getAreaColor(areaIndex),
            fontWeight: 600,
            fontSize: "0.7rem",
            minWidth: 60,
          }}
        >
          {areaLabel}:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {materialNames === null
            ? "?"
            : materialNames.length > 0
              ? materialNames.join(", ")
              : "—"}
        </Typography>
      </Box>
    );
  }
);
AreaMaterialsRow.displayName = "AreaMaterialsRow";
